// The marks on the map and the paper they sit on.
//
// Three colours, no more: paper, ink and a Baedeker red. Shape says what a
// church is (dot, cross, diamond, drop, hollow square); a ring says what the
// reader is doing with it (hover, selected); a faded mark says it is there
// but not what was asked for. Colour never carries a second meaning.

export const PAPER = '#fdf6ed';
export const INK = '#1d1c1c';
export const RED = '#bb3b37';

/** paper..ink at `x` in [0,1]. Every grey on the map is one of these. */
export function tint(x) {
  const a = hex(PAPER), b = hex(INK);
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * x));
  return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
}
function hex(h) { return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)); }

export const KIND_PRIORITY = { c: 2, b: 3, s: 4, m: 6, p: 5 };
export const KIND_SIZE = { c: 22, b: 18, s: 18, m: 12, p: 11 };

/** Draws one mark at 2x and returns { data, width, height } for map.addImage. */
export function drawMark(kind, { dim = false } = {}) {
  const size = KIND_SIZE[kind] || KIND_SIZE.p;
  const px = size * 2 + 8;
  const c = document.createElement('canvas');
  c.width = px; c.height = px;
  const g = c.getContext('2d');
  g.scale(2, 2);
  const cx = px / 4, cy = px / 4, r = size / 2;
  g.globalAlpha = dim ? 0.28 : 1;
  g.lineJoin = 'round';
  g.fillStyle = RED; g.strokeStyle = PAPER; g.lineWidth = 1.6;
  if (kind === 'c') {
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill(); g.stroke();
    g.strokeStyle = PAPER; g.lineWidth = 2.2; g.lineCap = 'round';
    g.beginPath(); g.moveTo(cx, cy - r * 0.62); g.lineTo(cx, cy + r * 0.62); g.moveTo(cx - r * 0.42, cy - r * 0.2); g.lineTo(cx + r * 0.42, cy - r * 0.2); g.stroke();
  } else if (kind === 'b') {
    g.beginPath(); g.moveTo(cx, cy - r); g.lineTo(cx + r, cy); g.lineTo(cx, cy + r); g.lineTo(cx - r, cy); g.closePath(); g.fill(); g.stroke();
    g.fillStyle = PAPER; g.beginPath(); g.arc(cx, cy, r * 0.22, 0, Math.PI * 2); g.fill();
  } else if (kind === 's') {
    g.beginPath(); g.moveTo(cx, cy - r); g.quadraticCurveTo(cx + r, cy - r * 0.1, cx + r * 0.72, cy + r * 0.35);
    g.arc(cx, cy + r * 0.35, r * 0.72, 0, Math.PI, false); g.quadraticCurveTo(cx - r, cy - r * 0.1, cx, cy - r); g.closePath(); g.fill(); g.stroke();
    g.fillStyle = PAPER; g.beginPath(); g.arc(cx, cy + r * 0.32, r * 0.2, 0, Math.PI * 2); g.fill();
  } else if (kind === 'm') {
    g.fillStyle = PAPER; g.strokeStyle = RED; g.lineWidth = 2;
    g.beginPath(); g.rect(cx - r + 1, cy - r + 1, 2 * r - 2, 2 * r - 2); g.fill(); g.stroke();
  } else {
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill(); g.stroke();
  }
  return { data: g.getImageData(0, 0, px, px).data, width: px, height: px };
}

export function registerMarks(map) {
  for (const k of ['p', 'c', 'b', 's', 'm']) {
    map.addImage('mk-' + k, drawMark(k), { pixelRatio: 2 });
    map.addImage('mk-' + k + '-dim', drawMark(k, { dim: true }), { pixelRatio: 2 });
  }
}

/** An inline SVG of the same mark for the legend, the list and the drawer. */
export function markSvg(kind, size = 14) {
  const r = size / 2, c = size / 2;
  const stroke = `stroke="${PAPER}" stroke-width="1.2"`;
  let body;
  if (kind === 'c') body = `<circle cx="${c}" cy="${c}" r="${r - 0.8}" fill="${RED}" ${stroke}/><path d="M${c} ${c - r * 0.6}V${c + r * 0.6}M${c - r * 0.42} ${c - r * 0.2}H${c + r * 0.42}" stroke="${PAPER}" stroke-width="1.8" stroke-linecap="round" fill="none"/>`;
  else if (kind === 'b') body = `<path d="M${c} 1 L${size - 1} ${c} L${c} ${size - 1} L1 ${c}Z" fill="${RED}" ${stroke}/><circle cx="${c}" cy="${c}" r="${r * 0.22}" fill="${PAPER}"/>`;
  else if (kind === 's') body = `<path d="M${c} 1 Q${size - 1} ${c * 0.9} ${c + r * 0.72} ${c + r * 0.35} A${r * 0.72} ${r * 0.72} 0 1 1 ${c - r * 0.72} ${c + r * 0.35} Q1 ${c * 0.9} ${c} 1Z" fill="${RED}" ${stroke}/>`;
  else if (kind === 'm') body = `<rect x="1.5" y="1.5" width="${size - 3}" height="${size - 3}" fill="${PAPER}" stroke="${RED}" stroke-width="1.8"/>`;
  else body = `<circle cx="${c}" cy="${c}" r="${r - 1.2}" fill="${RED}" ${stroke}/>`;
  return `<svg class="atlas-mark atlas-mark-${kind}" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" aria-hidden="true" focusable="false">${body}</svg>`;
}

/* ── The paper: OpenFreeMap's Positron, repainted in the three colours ──── */

const LAND = tint(0.045), PARK = tint(0.085), RESIDENTIAL = tint(0.02), BUILDING = tint(0.12);
const CASING = tint(0.26), CASING_HEAVY = tint(0.34), PATH = tint(0.22), RAIL = tint(0.3), BORDER = tint(0.42);
const LABEL = INK, LABEL_SOFT = tint(0.85);

export function repaint(style) {
  const layers = [];
  for (const l of style.layers) {
    const id = l.id, type = l.type;
    const paint = { ...(l.paint || {}) };
    const layout = { ...(l.layout || {}) };
    if (id.includes('glacier') || id.includes('ice_shelf') || id.startsWith('aeroway')) continue;
    if (type === 'background') paint['background-color'] = LAND;
    else if (type === 'fill') {
      if (id === 'water') paint['fill-color'] = PAPER;
      else if (id === 'park' || id.includes('wood')) paint['fill-color'] = PARK;
      else if (id.includes('residential')) paint['fill-color'] = RESIDENTIAL;
      else if (id === 'building') { paint['fill-color'] = BUILDING; delete paint['fill-outline-color']; paint['fill-opacity'] = ['interpolate', ['linear'], ['zoom'], 13, 0, 15, 0.7]; }
      else paint['fill-color'] = LAND;
      delete paint['fill-antialias'];
    } else if (type === 'line') {
      if (id.startsWith('waterway')) paint['line-color'] = PAPER;
      else if (id.startsWith('boundary')) { paint['line-color'] = BORDER; paint['line-dasharray'] = [3, 2]; }
      else if (id.startsWith('railway')) { paint['line-color'] = RAIL; if (paint['line-opacity'] === undefined) paint['line-opacity'] = 0.6; }
      else if (id.includes('casing')) paint['line-color'] = id.includes('motorway') ? CASING_HEAVY : CASING;
      else if (id.includes('path')) { paint['line-color'] = PATH; paint['line-dasharray'] = [2, 2]; }
      else if (id.includes('subtle')) paint['line-color'] = CASING;
      else paint['line-color'] = PAPER;
      delete paint['line-gradient'];
    } else if (type === 'symbol') {
      paint['text-color'] = id.startsWith('label_country') || id.startsWith('label_state') ? LABEL_SOFT : LABEL;
      paint['text-halo-color'] = PAPER; paint['text-halo-width'] = 1.6; paint['text-halo-blur'] = 0;
      delete paint['icon-color']; delete paint['icon-halo-color'];
      if (id.startsWith('highway-shield') || id.startsWith('road_shield')) continue;
      if (id.startsWith('highway-name')) paint['text-color'] = LABEL_SOFT;
      layout['text-letter-spacing'] = layout['text-letter-spacing'] || 0.02;
    }
    layers.push({ ...l, paint, layout });
  }
  return { ...style, layers };
}
